<template>
  <div class="advanced-query content mt-5 mb-7">
    <h2 class="mb-4">Consulta Avançada</h2>
    <v-row dense>
      <v-col cols="12" md="8">
        <v-alert type="info" outlined class="mb-4">
          Esta área é exclusiva para o full admin (admin@admin.com.br). Deletes, updates sem
          WHERE, DROP TABLE (exceto temporárias) e TRUNCATE estão bloqueados.
        </v-alert>
        <div class="d-flex align-center justify-space-between mb-2">
          <span class="font-weight-medium">Schemas disponíveis</span>
          <v-btn small text color="primary" @click="selectAllSchemas" :disabled="!schemas.length">
            Selecionar todos
          </v-btn>
        </div>
        <v-text-field
          v-model="schemaSearch"
          label="Buscar schemas"
          placeholder="Digite parte do schema"
          dense
          clearable
          class="mb-2"
          :disabled="loadingSchemas || running"
          @input="onSchemaSearch"
        ></v-text-field>
        <v-select
          v-model="form.schemas"
          :items="filteredSchemas"
          label="Schemas"
          multiple
          chips
          deletable-chips
          dense
          :search-input.sync="schemaSearch"
          @update:search-input="onSchemaSearch"
          :loading="loadingSchemas"
          :disabled="loadingSchemas || running"
          hint="Selecione um ou mais schemas para executar a consulta em sequência"
          persistent-hint
        ></v-select>
        <v-text-field
          class="mt-4"
          v-model="form.name"
          label="Nome da consulta"
          placeholder="Informe um nome para salvar"
          :disabled="running"
        ></v-text-field>
        <v-textarea
          v-model="form.query"
          label="Query"
          auto-grow
          rows="8"
          :disabled="running"
          :error-messages="validationStatus && !validationStatus.valid ? validationStatus.message : ''"
          :success-messages="validationStatus?.valid ? validationStatus.message : ''"
          hint="Você pode criar tabelas temporárias e executar selects em sequência"
          persistent-hint
        ></v-textarea>
        <div class="advanced-query__actions mt-2">
          <div class="d-flex align-center flex-wrap gap-2">
            <v-btn color="primary" @click="runQuery" :disabled="!canRun" :loading="running">
              Executar consulta
            </v-btn>
            <v-btn color="primary" outlined @click="validateQuery" :disabled="!canRun" :loading="validating">
              Validar query
            </v-btn>
            <v-btn color="secondary" outlined @click="saveQuery" :disabled="!canSave" :loading="saving">
              Salvar consulta
            </v-btn>
            <v-btn text color="primary" @click="clearForm" :disabled="running">
              Limpar
            </v-btn>
          </div>
          <v-btn color="success" outlined @click="exportResult" :disabled="!hasRows">
            Exportar XLSX
          </v-btn>
        </div>
        <v-alert v-if="validationStatus" :type="validationStatus.valid ? 'success' : 'error'" outlined class="mt-2">
          {{ validationStatus.message }}
        </v-alert>
        <v-card class="mt-4" outlined>
          <v-card-title class="justify-space-between">
            <span>Ferramentas de apoio</span>
            <v-btn small text color="primary" :loading="loadingTables" @click="fetchTables()" :disabled="!form.schemas.length">
              Atualizar tabelas
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-autocomplete
              v-model="selectedTable"
              :items="filteredTables"
              :search-input.sync="tableSearch"
              :loading="loadingTables"
              :disabled="!form.schemas.length || running"
              hide-no-data
              hide-selected
              dense
              label="Buscar tabelas"
              placeholder="Digite parte do schema ou da tabela"
              item-text="label"
              item-value="value"
              clearable
              @change="handleTableSelection"
              @update:search-input="onTableSearch"
            ></v-autocomplete>
            <v-skeleton-loader
              v-if="loadingColumns"
              type="list-item"
              class="mt-3"
            ></v-skeleton-loader>
            <div v-else-if="selectedTable && tableColumns.length" class="mt-3">
              <div class="d-flex align-center justify-space-between">
                <span class="font-weight-medium">Colunas de {{ selectedTable }}</span>
                <span class="grey--text text--darken-1">{{ tableColumns.length }} colunas</span>
              </div>
              <div class="advanced-query__chips mt-2">
                <v-chip
                  v-for="column in tableColumns"
                  :key="column.label"
                  small
                  class="ma-1"
                  outlined
                >
                  <span class="font-weight-medium">{{ column.label }}</span>
                  <span class="grey--text text--darken-1 ml-1">{{ column.type }}</span>
                </v-chip>
              </div>
            </div>
            <v-alert v-else-if="selectedTable" type="warning" outlined class="mt-3">
              Nenhuma coluna encontrada para a tabela selecionada.
            </v-alert>
          </v-card-text>
        </v-card>
        <v-card class="mt-4" outlined>
          <div class="advanced-query__table-wrapper">
            <v-data-table
              :headers="tableHeaders"
              :items="result?.rows || []"
              :loading="running"
              :items-per-page="10"
              dense
              class="advanced-query__table"
              no-data-text="Execute uma consulta para visualizar os resultados"
            >
              <template v-slot:body.append v-if="hasRows">
                <tr>
                  <td :colspan="tableHeaders.length" class="text-right font-weight-medium">
                    {{ result.rows.length }} registros encontrados
                  </td>
                </tr>
              </template>
            </v-data-table>
          </div>
        </v-card>
        <v-alert v-if="result && !hasRows && !running" type="warning" outlined class="mt-4">
          Nenhum registro encontrado para os filtros informados.
        </v-alert>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="advanced-query__history">
          <v-card-title class="justify-space-between">
            <span>Consultas salvas</span>
            <v-btn icon small @click="fetchSavedQueries" :loading="loadingHistory">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-0">
            <v-skeleton-loader
              v-if="loadingHistory"
              type="list-item-two-line"
              class="pa-4"
            ></v-skeleton-loader>
            <div v-else>
              <v-list two-line dense>
                <template v-if="savedQueries.length">
                  <v-list-item
                    v-for="query in savedQueries"
                    :key="query.advancedQueryId"
                    :class="{ 'advanced-query__history-item--active': form.id === query.advancedQueryId }"
                    @click="applySavedQuery(query)"
                  >
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">
                        {{ query.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ formatDate(query.createdAt) }}
                      </v-list-item-subtitle>
                      <div class="advanced-query__chips">
                        <v-chip
                          v-for="schema in query.schemas"
                          :key="schema"
                          small
                          class="ma-1"
                          color="primary"
                          text-color="white"
                          label
                        >
                          {{ schema }}
                        </v-chip>
                      </div>
                    </v-list-item-content>
                    <v-list-item-action class="advanced-query__history-actions">
                      <v-btn icon small color="primary" @click.stop="runSavedQuery(query)">
                        <v-icon small>mdi-play-circle</v-icon>
                      </v-btn>
                      <v-btn icon small color="error" @click.stop="deleteSavedQuery(query)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </template>
                <v-list-item v-else>
                  <v-list-item-content>
                    <v-list-item-title>Nenhuma consulta salva ainda.</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { advancedQueryAPI } from "../API/AdvancedQueryAPI";
import geraXls from "@/helpers/geraXls";

export default {
  name: "ConsultaAvancada",
  data() {
    return {
      schemas: [],
      form: {
        schemas: [],
        query: "",
        name: "",
        id: null,
      },
      result: null,
      savedQueries: [],
      loadingSchemas: false,
      loadingHistory: false,
      running: false,
      saving: false,
      loadingTables: false,
      loadingColumns: false,
      validating: false,
      tables: [],
      tableColumns: [],
      selectedTable: null,
      tableSearch: "",
      tableSearchTimeout: null,
      schemaSearch: "",
      validationStatus: null,
    };
  },
  computed: {
    canRun() {
      return this.form.schemas.length > 0 && this.form.query.trim().length > 0;
    },
    canSave() {
      return this.canRun && this.form.name.trim().length > 0;
    },
    hasRows() {
      return this.result && Array.isArray(this.result.rows) && this.result.rows.length > 0;
    },
    tableHeaders() {
      if (!this.result || !Array.isArray(this.result.columns)) return [];
      return this.result.columns.map((column) => ({
        text: this.formatHeader(column),
        value: column,
        sortable: false,
      }));
    },
    filteredTables() {
      if (!this.tableSearch) return this.tables;
      const term = this.tableSearch.toLowerCase();
      return this.tables.filter((table) => table.label.toLowerCase().includes(term));
    },
    filteredSchemas() {
      const normalized = (this.schemaSearch || "").trim();
      if (!normalized) return this.schemas;
      const term = normalized.toLowerCase();
      return this.schemas.filter((schema) => schema.toLowerCase().includes(term));
    },
  },
  methods: {
    async fetchSchemas() {
      this.loadingSchemas = true;
      try {
        const { data } = await advancedQueryAPI.listSchemas();
        this.schemas = data || [];
      } catch (error) {
        this.notifyError(error, "Não foi possível carregar os schemas");
      } finally {
        this.loadingSchemas = false;
      }
    },
    async fetchSavedQueries() {
      this.loadingHistory = true;
      try {
        const { data } = await advancedQueryAPI.listSavedQueries();
        this.savedQueries = data || [];
      } catch (error) {
        this.notifyError(error, "Não foi possível carregar as consultas salvas");
      } finally {
        this.loadingHistory = false;
      }
    },
    async fetchTables(searchTerm = null) {
      if (!this.form.schemas.length) {
        this.tables = [];
        return;
      }

      this.loadingTables = true;
      try {
        const { data } = await advancedQueryAPI.listTables(this.form.schemas, searchTerm);
        this.tables = (data || []).map((table) => this.normalizeTable(table));
      } catch (error) {
        this.notifyError(error, "Não foi possível carregar as tabelas");
      } finally {
        this.loadingTables = false;
      }
    },
    async fetchColumns(tableIdentifier) {
      if (!tableIdentifier) return;
      const [schema, table] = tableIdentifier.split(".");
      this.loadingColumns = true;
      this.tableColumns = [];
      try {
        const { data } = await advancedQueryAPI.listColumns(schema, table);
        this.tableColumns = (data || []).map((column) => ({
          label: column.name || column.Name,
          type: column.dataType || column.DataType || "",
        }));
      } catch (error) {
        this.notifyError(error, "Não foi possível carregar as colunas da tabela");
      } finally {
        this.loadingColumns = false;
      }
    },
    onTableSearch(term) {
      const normalizedTerm = term ? term.trim() : "";
      this.tableSearch = normalizedTerm;
      if (this.tableSearchTimeout) {
        clearTimeout(this.tableSearchTimeout);
      }

        this.tableSearchTimeout = setTimeout(() => {
          const searchTerm = normalizedTerm || null;
          this.fetchTables(searchTerm);
        }, 300);
    },
    onSchemaSearch(term) {
      this.schemaSearch = (term || "").trim();
    },
    handleTableSelection(value) {
      this.selectedTable = value;
      if (value) {
        this.fetchColumns(value);
      } else {
        this.tableColumns = [];
      }
    },
    async runQuery() {
      if (!this.canRun) return;
      this.running = true;
      try {
        const payload = {
          schemas: this.form.schemas,
          query: this.form.query,
          savedQueryId: this.form.id,
        };
        const { data } = await advancedQueryAPI.runQuery(payload);
        this.result = {
          columns: data?.columns || [],
          rows: data?.rows || [],
        };
        this.$notification.success("Consulta executada com sucesso");
      } catch (error) {
        this.result = null;
        this.notifyError(error, "Não foi possível executar a consulta");
      } finally {
        this.running = false;
      }
    },
    async validateQuery() {
      if (!this.canRun) return;
      this.validating = true;
      this.validationStatus = null;
      try {
        const payload = {
          schemas: this.form.schemas,
          query: this.form.query,
          savedQueryId: this.form.id,
        };
        const { data } = await advancedQueryAPI.validateQuery(payload);
        this.validationStatus = {
          valid: true,
          message: data?.message || "Query válida",
        };
        this.$notification.success("Query validada com sucesso");
      } catch (error) {
        const message = error?.response?.data || error?.message || "Não foi possível validar a consulta";
        this.validationStatus = {
          valid: false,
          message,
        };
        this.$notification.error(message);
      } finally {
        this.validating = false;
      }
    },
    async saveQuery() {
      if (!this.canSave) return;
      this.saving = true;
      try {
        const payload = {
          advancedQueryId: this.form.id,
          name: this.form.name,
          queryText: this.form.query,
          schemas: this.form.schemas,
        };
        const { data } = await advancedQueryAPI.saveQuery(payload);
        this.form.id = data?.advancedQueryId;
        await this.fetchSavedQueries();
        this.$notification.success("Consulta salva com sucesso");
      } catch (error) {
        this.notifyError(error, "Não foi possível salvar a consulta");
      } finally {
        this.saving = false;
      }
    },
    applySavedQuery(query) {
      this.form = {
        schemas: [...(query.schemas || [])],
        query: query.queryText,
        name: query.name,
        id: query.advancedQueryId,
      };
      this.result = null;
    },
    runSavedQuery(query) {
      this.applySavedQuery(query);
      this.runQuery();
    },
    async deleteSavedQuery(query) {
      if (!query?.advancedQueryId) return;
      const confirmed = window.confirm(`Deseja excluir a consulta "${query.name}"?`);
      if (!confirmed) return;

      try {
        await advancedQueryAPI.deleteSavedQuery(query.advancedQueryId);
        this.$notification.success("Consulta excluída com sucesso");
        if (this.form.id === query.advancedQueryId) {
          this.clearForm();
        }
        await this.fetchSavedQueries();
      } catch (error) {
        this.notifyError(error, "Não foi possível excluir a consulta");
      }
    },
    selectAllSchemas() {
      this.form.schemas = [...this.schemas];
    },
    clearForm() {
      this.form = {
        schemas: [],
        query: "",
        name: "",
        id: null,
      };
      this.result = null;
      this.tables = [];
      this.tableColumns = [];
      this.selectedTable = null;
      this.tableSearch = "";
      this.schemaSearch = "";
      this.validationStatus = null;
    },
    exportResult() {
      if (!this.hasRows) return;
      geraXls(this.result.rows, "consulta_avancada", this.tableHeaders);
    },
    formatHeader(column) {
      if (!column) return "";
      if (column.toLowerCase() === "schema") return "Schema";
      return column
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    formatDate(date) {
      if (!date) return "";
      const parsed = new Date(date);
      if (Number.isNaN(parsed.getTime())) return date;
      return parsed.toLocaleString("pt-BR");
    },
    notifyError(error, fallback) {
      const message = error?.response?.data || error?.message || fallback;
      this.$notification.error(message);
    },
    normalizeTable(table) {
      const schema = table.schema || table.Schema;
      const name = table.table || table.Table;
      const label = `${schema}.${name}`;
      return {
        ...table,
        schema,
        table: name,
        label,
        value: label,
      };
    },
  },
  watch: {
    "form.schemas"() {
      this.validationStatus = null;
      this.tables = [];
      this.tableColumns = [];
      this.selectedTable = null;
      this.tableSearch = "";
      this.schemaSearch = "";
      if (this.form.schemas.length) {
        this.fetchTables();
      }
    },
    "form.query"() {
      this.validationStatus = null;
    },
  },
  mounted() {
    this.fetchSchemas();
    this.fetchSavedQueries();
  },
};
</script>

<style scoped lang="scss">
.advanced-query__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.advanced-query__table-wrapper {
  max-height: 500px;
  overflow: auto;
}

.advanced-query__table {
  min-width: 100%;
}

.advanced-query__table ::v-deep th,
.advanced-query__table ::v-deep td {
  max-width: 10ch;
  width: 10ch;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  resize: horizontal;
}

.advanced-query__history {
  max-height: 100%;
}

.advanced-query__chips {
  display: flex;
  flex-wrap: wrap;
}

.advanced-query__history-actions {
  display: flex;
  gap: 0.25rem;
}

.advanced-query__history-item--active {
  background-color: rgba(33, 150, 243, 0.12);
}

.gap-2 {
  gap: 0.5rem;
}
</style>
